"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { JobProgress } from "@/components/jobs/job-progress";
import { ApiError, api, apiDownload, loginUrl } from "@/lib/api/client";
import type { LinkingStarted, LinkingSuggestion, LinkingTargetEstimate, LinkingTargetResult, Project } from "@/lib/api/types";
import { linkTargetCost } from "@/lib/costs";
import { saveBlob } from "@/lib/download";
import { formatCount } from "@/lib/format";
import { formatScore, shortUrl } from "@/lib/links";

import { SuggestionCard } from "./suggestion-card";

const SIMILARITY_CHOICES = [0.6, 0.65, 0.7, 0.75, 0.8];
const FIELD = "h-11 rounded-md border border-field bg-white px-3 text-sm";

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : "Unbekannter Fehler";
}

type Props = {
  project: Project;
  /** Es gibt einen Crawl mit Links (sonst ist die Prüfung gesperrt). */
  available: boolean;
  /** Die Gesamtanalyse läuft gerade: zwei Läufe gleichzeitig starten nicht. */
  blocked: boolean;
  /** Mindest-Ähnlichkeit des Servers (`LINKING_EMBEDDING_MIN_SIMILARITY`). */
  defaultSimilarity: number;
};

/** „Zielseite prüfen“: eine URL angeben, die Seiten finden, die semantisch dazu passen und noch nicht verlinken (SPEC 4.6). */
export function TargetPanel({ project, available, blocked, defaultSimilarity }: Props) {
  const router = useRouter();
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<LinkingTargetResult | null>(null);
  const [suggestions, setSuggestions] = useState<LinkingSuggestion[] | null>(null);
  const [estimate, setEstimate] = useState<LinkingTargetEstimate | null>(null);
  const [similarity, setSimilarity] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  const load = useCallback(
    () =>
      api<LinkingTargetResult>(`/api/linking/target?project_id=${project.id}`)
        .then((next) => {
          setResult(next);
          setSuggestions(next.suggestions);
          setLoadError(null);
        })
        .catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) {
            router.replace(loginUrl("/links"));
            return;
          }
          setLoadError(message(caught));
        }),
    [project.id, router],
  );

  useEffect(() => {
    void load();
  }, [load, reload]);

  const running = result?.run?.state === "running";
  const locked = !available || blocked || running;

  const check = async () => {
    setBusy(true);
    setError(null);
    setEstimate(null);
    try {
      setEstimate(await api<LinkingTargetEstimate>(`/api/linking/target/estimate?project_id=${project.id}&url=${encodeURIComponent(url.trim())}`));
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (!estimate) return;
    setBusy(true);
    setError(null);
    try {
      await api<LinkingStarted>("/api/linking/runs", {
        method: "POST",
        json: { project_id: project.id, target_url: estimate.url, ...(similarity === null ? {} : { min_similarity: similarity }) },
      });
      setEstimate(null);
      setReload((current) => current + 1);
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (item: LinkingSuggestion, status: LinkingSuggestion["status"]) => {
    setBusy(true);
    setError(null);
    try {
      const next = await api<LinkingSuggestion>(`/api/linking/suggestions/${item.id}?project_id=${project.id}`, { method: "PATCH", json: { status } });
      setSuggestions((list) => (list ?? []).map((entry) => (entry.id === next.id ? { ...entry, status: next.status } : entry)));
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    setError(null);
    try {
      const file = await apiDownload(`/api/linking/export.csv?project_id=${project.id}&scope=ziel`, "interne-links-zielseite.csv");
      saveBlob(file.blob, file.filename);
    } catch (caught) {
      setError(message(caught));
    }
  };

  const items = suggestions ?? [];
  const summary = result?.summary;
  const minSimilarity = similarity ?? defaultSimilarity;
  const choices = [...new Set([defaultSimilarity, ...SIMILARITY_CHOICES])].sort((a, b) => a - b);

  return (
    <section aria-labelledby="target-title" className="flex flex-col gap-4 rounded-lg border border-line bg-white px-5 py-4">
      <div className="flex flex-col gap-1">
        <h2 id="target-title" className="text-base font-semibold">
          Zielseite prüfen
        </h2>
        <p className="text-[13px] text-muted">Gib eine Seite von {project.domain} an. Die Prüfung zeigt, von welchen anderen Seiten sie inhaltlich passend verlinkt werden kann.</p>
      </div>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void check();
        }}
      >
        <div className="flex min-w-[260px] flex-1 flex-col gap-1">
          <label htmlFor="target-url" className="text-[13px] font-medium">
            Zielseite (URL)
          </label>
          <input
            id="target-url"
            type="url"
            inputMode="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder={`https://${project.domain}/…`}
            className={`${FIELD} w-full`}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="target-similarity" className="text-[13px] font-medium">
            Mindest-Ähnlichkeit
          </label>
          <select id="target-similarity" value={minSimilarity} onChange={(event) => setSimilarity(Number(event.target.value))} className={FIELD}>
            {choices.map((choice) => (
              <option key={choice} value={choice}>
                {formatScore(choice)}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" disabled={busy || locked || url.trim() === ""} className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white disabled:opacity-60">
          Prüfen
        </button>
      </form>

      {blocked && <p className="text-[13px] text-muted">Die Gesamtanalyse läuft gerade. Danach kannst du eine Zielseite prüfen.</p>}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      {estimate && (
        <div role="group" aria-label="Prüfung der Zielseite bestätigen" className="flex flex-col gap-3 rounded-md border border-line px-4 py-3 text-sm">
          <p>
            <strong className="font-medium">Zielseite {shortUrl(estimate.url)} prüfen?</strong>
            {estimate.title ? ` „${estimate.title}“.` : ""} Alle {formatCount(estimate.pages)} indexierbaren Seiten des Crawls werden mit Gemini-Embeddings verglichen. Kosten:{" "}
            {linkTargetCost(estimate, project.domain)}. Zeigt höchstens Seiten, die noch nicht im Text verlinken.
          </p>
          {estimate.reason && (
            <p role="status" className="rounded-md bg-warning-bg px-3 py-2 text-warning">
              {estimate.reason}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" disabled={busy || !estimate.connected} onClick={() => void start()} className="h-11 rounded-md bg-accent px-3.5 text-[13px] font-medium text-white disabled:opacity-60">
              Prüfung starten
            </button>
            <button type="button" onClick={() => setEstimate(null)} className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px]">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {running && result?.run?.job_id && (
        <JobProgress key={result.run.job_id} jobId={result.run.job_id} title="Zielseite wird geprüft" onFinished={() => setReload((current) => current + 1)} />
      )}
      {result?.run?.state === "failed" && (
        <p role="alert" className="rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          Die Prüfung ist fehlgeschlagen{result.run.error ? `: ${result.run.error}` : "."}
        </p>
      )}
      {loadError && <p className="text-sm text-danger">Das letzte Ergebnis konnte nicht geladen werden: {loadError}</p>}

      {result && result.target_url && summary && (
        <section aria-labelledby="target-result-title" className="flex flex-col rounded-lg border border-line">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#e6e9e7] px-5 py-3">
            <div className="flex min-w-0 flex-col gap-0.5">
              <h3 id="target-result-title" className="text-base font-semibold">
                Quellen für <span className="break-all font-mono text-accent">{shortUrl(result.target_url)}</span>
              </h3>
              <span className="text-xs text-muted">
                {result.target_title ? `${result.target_title} · ` : ""}
                {formatCount(summary.suggestions)} passende {summary.suggestions === 1 ? "Quelle" : "Quellen"} · {formatCount(summary.linked)} verlinken schon im Text ·{" "}
                {formatCount(summary.embedded)} neu eingebettet, {formatCount(summary.cached)} aus dem Speicher
              </span>
            </div>
            <button
              type="button"
              disabled={items.length === 0}
              onClick={() => void exportCsv()}
              className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
            >
              CSV exportieren
            </button>
          </div>
          {items.length === 0 && <p className="px-5 py-6 text-sm text-muted">Keine passende Seite ohne Link gefunden. Mit einer niedrigeren Mindest-Ähnlichkeit erneut prüfen.</p>}
          {items.map((item) => (
            <SuggestionCard key={item.id} item={item} busy={busy} onDecide={(status) => void decide(item, status)} />
          ))}
        </section>
      )}
    </section>
  );
}
